// Signup totals vs. games logged later — the pure decision behind the Home
// screen's "counted twice" prompt. Tested by scripts/test-signup-overlap.ts.
//
// A coach joining mid-season can enter a starting record (team.seasonStartRecord)
// and import season-to-date stats (the `source: 'onboardingImport'` container
// game). Both stand in for games the coach will NOT enter in the app. If they
// later log those same games, the banner record and every counting stat are
// counted twice. Games dated before the team was created can't be new results
// on top of the signup totals, so once enough of them are logged to cover what
// was entered at signup, the signup copy is redundant.
//
// Framework-free: no Firebase imports, so the test script can load it directly.

export interface SignupOverlap {
  /** Logged pre-signup results cover the starting record. */
  recordOverlaps: boolean;
  /** Logged pre-signup per-game stats cover the signup stats import. */
  statsOverlap: boolean;
}

export function detectSignupOverlap(
  team: any,
  completedGames: any[],
  onboardingStatsGame: any | null,
): SignupOverlap {
  const none = { recordOverlaps: false, statsOverlap: false };
  const joinedAt = team?.createdAt ? new Date(team.createdAt).getTime() : NaN;
  if (isNaN(joinedAt)) return none;

  const preJoin = completedGames.filter(g =>
    g?.source !== 'onboardingImport' && !g?.isPractice && g?.result &&
    new Date(g.date).getTime() < joinedAt
  );

  const start = team?.seasonStartRecord;
  const startTotal = (start?.w ?? 0) + (start?.l ?? 0) + (start?.t ?? 0);
  const recordOverlaps = startTotal > 0 && preJoin.length >= startTotal;

  // The CSV's GP column is games played at signup. Once at least that many
  // pre-signup games carry their own imported stats, the container is a copy.
  const signupGamesPlayed = Math.max(0, ...Object.values(onboardingStatsGame?.stats ?? {})
    .map((s: any) => Number(s?.gp) || 0));
  const withStats = preJoin.filter(g => g.stats && Object.keys(g.stats).length > 0).length;
  const statsOverlap = !!onboardingStatsGame && signupGamesPlayed > 0 && withStats >= signupGamesPlayed;

  return { recordOverlaps, statsOverlap };
}

/**
 * Per-player stat lines from every real game, leaving out the signup container,
 * ready for aggregatePlayerStats. Only players named in the container are
 * returned, and a player with no per-game lines is left out entirely so the
 * caller keeps their signup totals rather than wiping them to nothing.
 */
export function statLinesWithoutSignup(gameDocs: any[], onboardingStatsGame: any): Record<string, any[]> {
  const series: Record<string, any[]> = {};
  for (const g of gameDocs) {
    if (g.id === onboardingStatsGame.id || g.source === 'onboardingImport' || g.isPractice || !g.stats) continue;
    for (const [pid, line] of Object.entries(g.stats)) (series[pid] ||= []).push(line);
  }
  const out: Record<string, any[]> = {};
  for (const pid of Object.keys(onboardingStatsGame.stats ?? {})) {
    if (series[pid]?.length) out[pid] = series[pid];
  }
  return out;
}
